
import { UserDropdownMenu } from '../../../components/ui/UserDropdownMenu';

interface DashboardHeaderProps {
  activeSection: string;
  setActiveSection: (section: string) => void;
  isMobileMenuOpen: boolean;
  setIsMobileMenuOpen: (open: boolean) => void;
}

export default function DashboardHeader({ 
  activeSection, 
  setActiveSection, 
  isMobileMenuOpen, 
  setIsMobileMenuOpen 
}: DashboardHeaderProps) {
  const menuItems = [
    { 
      id: 'dashboard', 
      label: 'Dashboard', 
      icon: 'ri-dashboard-3-fill',
      description: 'Vista general de tu situación financiera',
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    { 
      id: 'patrimonio', 
      label: 'Patrimonio', 
      icon: 'ri-safe-2-fill',
      description: 'Gestiona y supervisa tu patrimonio neto',
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-100'
    },
    { 
      id: 'ingresos', 
      label: 'Ingresos', 
      icon: 'ri-arrow-up-circle-fill',
      description: 'Registra y controla tus fuentes de ingresos',
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    },
    { 
      id: 'egresos', 
      label: 'Egresos', 
      icon: 'ri-arrow-down-circle-fill',
      description: 'Controla tus gastos y expensas diarias',
      color: 'text-red-600',
      bgColor: 'bg-red-100'
    },
    { 
      id: 'inversiones', 
      label: 'Inversiones', 
      icon: 'ri-line-chart-fill',
      description: 'Administra tu cartera de inversiones',
      color: 'text-purple-600',
      bgColor: 'bg-purple-100'
    },
    { 
      id: 'resumenes', 
      label: 'Resúmenes', 
      icon: 'ri-file-chart-2-fill',
      description: 'Análisis detallado mensual de tus finanzas',
      color: 'text-orange-600',
      bgColor: 'bg-orange-100'
    },
    { 
      id: 'reportes', 
      label: 'Reportes', 
      icon: 'ri-pie-chart-2-fill',
      description: 'Reportes avanzados y estadísticas',
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-100'
    }
  ];

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 fixed top-0 left-0 right-0 z-50">
      <div className="px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Left side: Hamburger Menu + Logo */}
          <div className="flex items-center space-x-4">
            {/* Hamburger Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="flex items-center justify-center w-10 h-10 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 cursor-pointer transition-colors shadow-md"
            >
              <div className="w-6 h-6 flex items-center justify-center relative">
                <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
                {/* Fallback usando caracteres Unicode */}
                <span className="absolute inset-0 flex items-center justify-center text-lg font-bold" style={{ fontFamily: 'monospace' }}>
                  {isMobileMenuOpen ? '×' : '≡'}
                </span>
              </div>
            </button>
            
            {/* Logo */}
            <h1 className="text-2xl font-bold text-emerald-600" style={{ fontFamily: '"Pacifico", serif' }}>
              Invirtiendo
            </h1>
          </div>

          {/* Right side: Section Display + User Menu */}
          <div className="flex items-center space-x-4">
            {/* Current Section Display */}
            <span className="text-lg font-medium text-gray-700">
              {menuItems.find(item => item.id === activeSection)?.label}
            </span>
            
            {/* User Dropdown Menu */}
            <UserDropdownMenu onNavigate={setActiveSection} />
          </div>
        </div>

        {/* Overlay */}
        {isMobileMenuOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-[55]"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}

        {/* Sidebar Menu */}
        <nav className={`fixed top-0 left-0 h-full w-96 bg-gradient-to-b from-gray-50 to-white shadow-2xl z-[60] transform transition-transform duration-300 ease-in-out ${
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}>
          <div className="p-6">
            {/* Sidebar Header */}
            <div className="relative mb-8">
              <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl p-6 text-white shadow-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: '"Pacifico", serif' }}>
                      Invirtiendo
                    </h2>
                    <p className="text-emerald-100 text-sm">
                      Tu plataforma financiera personal
                    </p>
                  </div>
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="p-2 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors cursor-pointer"
                  >
                    <i className="ri-close-line text-xl"></i>
                  </button>
                </div>
                {/* Decorative elements */}
                <div className="absolute -top-2 -right-2 w-16 h-16 bg-white/10 rounded-full"></div>
                <div className="absolute -bottom-3 -left-3 w-12 h-12 bg-white/5 rounded-full"></div>
              </div>
            </div>

            {/* Menu Items */}
            <div className="space-y-3">
              {menuItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveSection(item.id);
                    setIsMobileMenuOpen(false);
                  }}
                  className={`w-full group transition-all duration-200 cursor-pointer ${
                    activeSection === item.id
                      ? 'transform scale-[1.02]'
                      : 'hover:transform hover:scale-[1.01]'
                  }`}
                >
                  <div className={`flex items-start p-4 rounded-xl border-2 transition-all duration-200 ${
                    activeSection === item.id
                      ? `bg-gradient-to-r from-${item.bgColor.replace('bg-', '')}-50 to-${item.bgColor.replace('bg-', '')}-100 border-${item.color.replace('text-', '')}-200 shadow-lg`
                      : 'bg-white border-gray-100 hover:border-gray-200 hover:shadow-md'
                  }`}>
                    <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center mr-4 transition-all duration-200 ${
                      activeSection === item.id 
                        ? `${item.bgColor} ${item.color} shadow-md` 
                        : `bg-gray-100 text-gray-500 group-hover:${item.bgColor} group-hover:${item.color}`
                    }`}>
                      <i className={`${item.icon} text-xl`}></i>
                    </div>
                    <div className="flex-1 text-left">
                      <h3 className={`font-bold text-base mb-1 transition-colors duration-200 ${
                        activeSection === item.id 
                          ? item.color 
                          : 'text-gray-800 group-hover:text-gray-900'
                      }`}>
                        {item.label}
                      </h3>
                      <p className={`text-sm leading-tight transition-colors duration-200 ${
                        activeSection === item.id 
                          ? 'text-gray-600' 
                          : 'text-gray-500 group-hover:text-gray-600'
                      }`}>
                        {item.description}
                      </p>
                    </div>
                    {activeSection === item.id && (
                      <div className={`flex-shrink-0 w-1 h-8 ${item.color.replace('text-', 'bg-')} rounded-full ml-2`}></div>
                    )}
                  </div>
                </button>
              ))}
            </div>

            {/* Sidebar Footer */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center">
                    <i className="ri-lightbulb-flash-fill text-white text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 text-sm">💡 Tip del día</h4>
                    <p className="text-xs text-gray-600 mt-1">
                      Revisa la sección Resúmenes para obtener insights sobre tus finanzas
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 text-center">
                <p className="text-xs text-gray-400">
                  Invirtiendo v1.0 • Hecho con ❤️
                </p>
              </div>
            </div>
          </div>
        </nav>
      </div>
    </header>
  );
}
